import { Inject, Injectable, Optional, InjectionToken } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);

export const MAT_DAYJS_DATE_ADAPTER_OPTIONS = new InjectionToken<MatDayjsDateAdapterOptions>(
  'MAT_DAYJS_DATE_ADAPTER_OPTIONS',
);

export interface MatDayjsDateAdapterOptions {
  useUtc?: boolean;
}

@Injectable()
export class DayjsDateAdapter extends DateAdapter<Dayjs> {
  private useUtc = true; // Por defeito usamos UTC conforme o resto da app

  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) _matDateLocale: string,
    @Optional() @Inject(MAT_DAYJS_DATE_ADAPTER_OPTIONS) _options?: MatDayjsDateAdapterOptions,
  ) {
    super();
    this.useUtc = _options?.useUtc !== false;
    this.setLocale(_matDateLocale || 'pt');
  }

  private _dayjs(date?: any): Dayjs {
    return this.useUtc ? dayjs.utc(date) : dayjs(date);
  }

  getYear(date: Dayjs): number {
    return this._dayjs(date).year();
  }

  getMonth(date: Dayjs): number {
    return this._dayjs(date).month();
  }

  getDate(date: Dayjs): number {
    return this._dayjs(date).date();
  }

  getDayOfWeek(date: Dayjs): number {
    return this._dayjs(date).day();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const localeData = this._dayjs().localeData();
    return style === 'long' ? localeData.months() : localeData.monthsShort();
  }

  getDateNames(): string[] {
    const days = [];
    for (let i = 1; i <= 31; i++) days.push(String(i));
    return days;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const localeData = this._dayjs().localeData();
    if (style === 'long') return localeData.weekdays();
    if (style === 'short') return localeData.weekdaysShort();
    return localeData.weekdaysMin();
  }

  getYearName(date: Dayjs): string {
    return String(this._dayjs(date).year());
  }

  getFirstDayOfWeek(): number {
    return this._dayjs().localeData().firstDayOfWeek();
  }

  getNumDaysInMonth(date: Dayjs): number {
    return this._dayjs(date).daysInMonth();
  }

  clone(date: Dayjs): Dayjs {
    return this._dayjs(date).clone();
  }

  createDate(year: number, month: number, date: number): Dayjs {
    if (month < 0 || month > 11) {
      throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
    }
    if (date < 1 || date > this.getNumDaysInMonth(this._dayjs().set('year', year).set('month', month))) {
      throw Error(
        `Invalid date "${date}". Date has to be between 1 and ${this.getNumDaysInMonth(this._dayjs().set('year', year).set('month', month))}.`,
      );
    }

    const result = this._dayjs().set('year', year).set('month', month).set('date', date).startOf('day');

    return result.isValid() ? result : this.invalid();
  }

  today(): Dayjs {
    return this._dayjs().startOf('day');
  }

  parse(value: any, parseFormat: any): Dayjs | null {
    if (value && typeof value === 'string') {
      // 1. Tentar com o formato configurado no MAT_DATE_FORMATS
      let d = (dayjs as any).utc(value, parseFormat, this.locale);
      if (d.isValid()) return d.startOf('day');

      // 2. Se falhar, tentar formatos comuns de input manual em Portugal e ISO
      const manualFormats = ['DDMMYYYY', 'DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];
      for (const format of manualFormats) {
        let dManual = (dayjs as any).utc(value, format, this.locale);
        if (dManual.isValid()) return dManual.locale(this.locale).startOf('day');
      }

      // 3. Se falhar, tentar parse genérico (ISO 8601, etc)
      let d2 = (dayjs as any).utc(value);
      if (d2.isValid()) return d2.locale(this.locale).startOf('day');

      return null;
    }
    return value ? (dayjs as any).utc(value).locale(this.locale).startOf('day') : null;
  }

  format(date: Dayjs, displayFormat: any): string {
    const d = this._dayjs(date).locale(this.locale);
    if (!d.isValid()) return '';

    // Se não houver formato de exibição específico, usar o padrão do site
    if (!displayFormat || typeof displayFormat !== 'string') {
      return d.format('DD-MM-YYYY');
    }

    // Gerar string formatada
    let formatted = d.format(displayFormat);

    return formatted;
  }

  addCalendarYears(date: Dayjs, years: number): Dayjs {
    return this._dayjs(date).add(years, 'year');
  }

  addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return this._dayjs(date).add(months, 'month');
  }

  addCalendarDays(date: Dayjs, days: number): Dayjs {
    return this._dayjs(date).add(days, 'day');
  }

  toIso8601(date: Dayjs): string {
    return this._dayjs(date).toISOString();
  }

  /**
   * Converte qualquer valor recebido (string ISO, Date, number, Dayjs) para Dayjs.
   * Essencial para a compatibilidade com ngModel que pode receber valores
   * em qualquer formato (vindos da API como strings, ou convertidos para Date).
   * Sem este override, o matDatepicker recebia strings/Date e falhava a validação
   * com erro matDatepickerParse (bug residual da migração moment→dayjs).
   */
  override deserialize(value: any): Dayjs | null {
    if (value == null || value === '') {
      return null;
    }

    // Já é Dayjs — devolver directamente se válido
    if (dayjs.isDayjs(value)) {
      return value.isValid() ? value : null;
    }

    // Date object — converter para Dayjs UTC, preservando os valores locais de Y/M/D
    // Em vez de dayjs.utc(value) (que usa o epoch UTC e retrocede a data se GMT+X), usamos dayjs(value).utc(true)
    if (value instanceof Date) {
      const d = dayjs(value).utc(true);
      return d.isValid() ? d.startOf('day') : null;
    }

    // String — priorizar formatos manuais primeiro, depois tentar ISO
    if (typeof value === 'string') {
      // Formato DD-MM-YYYY (formato PT usado no site)
      let d = dayjs.utc(value, 'DD-MM-YYYY', true);
      if (d.isValid()) return d.startOf('day');

      // Formato DD/MM/YYYY (comum em PT)
      d = dayjs.utc(value, 'DD/MM/YYYY', true);
      if (d.isValid()) return d.startOf('day');

      // Formato DDMMYYYY (input sem separadores)
      d = dayjs.utc(value, 'DDMMYYYY', true);
      if (d.isValid()) return d.startOf('day');

      // ISO 8601 (e.g. '1997-11-10T00:00:00.000Z') ou fallback
      // Em vez de dayjs.utc(value), usamos dayjs(value).utc(true) para preservar a data local
      d = dayjs(value).utc(true);
      if (d.isValid()) return d.startOf('day');

      return null;
    }

    // Número (timestamp)
    if (typeof value === 'number') {
      const d = dayjs.utc(value);
      return d.isValid() ? d.startOf('day') : null;
    }

    return null;
  }

  isDateInstance(obj: any): boolean {
    if (obj == null) return false;
    if (dayjs.isDayjs(obj)) return true;
    if (obj instanceof Date) return true;
    // Strings com dígitos podem ser datas — o deserialize valida correctamente
    if (typeof obj === 'string' && obj.length > 0 && /\d/.test(obj)) return true;
    return false;
  }

  isValid(date: Dayjs): boolean {
    return this._dayjs(date).isValid();
  }

  override compareDate(first: Dayjs, second: Dayjs): number {
    const d1 = this._dayjs(first);
    const d2 = this._dayjs(second);
    if (!d1.isValid() || !d2.isValid()) return 0;
    if (d1.isBefore(d2, 'day')) return -1;
    if (d1.isAfter(d2, 'day')) return 1;
    return 0;
  }

  invalid(): Dayjs {
    return dayjs(null);
  }

  override setLocale(locale: any) {
    const normalizedLocale = locale === 'pt-PT' ? 'pt' : locale;
    super.setLocale(normalizedLocale);
    dayjs.locale(normalizedLocale);
  }
}
